import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarOff, Loader2, PhoneCall, Users, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  requestLeave, myLeave, listLeave, leaveImpact, approveLeave, rejectLeave, cancelLeave,
  myCovers, exotelPresence, getRoster, leaveBalance, leaveBalances, getLeavePolicy, saveLeavePolicy, setJoinedOn, setEmployment,
} from "@/lib/api";

// Staff leave. Everyone: ask for leave (full days or part of a day) and see what you're covering
// for others. Facility heads: approve it and choose who covers the person's sessions, clients and
// calls. Covers last only for the leave; clients go back to their coach by themselves.

const field = "bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100";
const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const KINDS = { annual: "Annual", sick: "Sick", personal: "Personal", bereavement: "Bereavement", other: "Other" };
const TONES = { pending: "amber", approved: "emerald", rejected: "rose", cancelled: "zinc", taken: "zinc" };

function Badge({ tone, children }) {
  const tones = { rose: "border-rose-500/40 bg-rose-500/10 text-rose-300", amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", zinc: "border-[#2a2a2a] text-zinc-400" };
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] ${tones[tone]}`}>{children}</span>;
}

const changed = () => window.dispatchEvent(new Event("leave-changed"));   // the menu's count refreshes
const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

export function when(lv) {
  const days = lv.start_date === lv.end_date ? fmtDay(lv.start_date) : `${fmtDay(lv.start_date)} – ${fmtDay(lv.end_date)}`;
  return lv.partial ? `${days}, ${lv.start_time}–${lv.end_time}` : `${days}${lv.start_date === lv.end_date ? " (full day)" : ""}`;
}

function AskForm({ staff, onDone }) {
  const [f, setF] = useState({ kind: "annual", start_date: "", end_date: "", partial: false, start_time: "14:00", end_time: "17:00", reason: "", user_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async () => {
    if (!f.start_date) return toast.error("Pick the first day");
    setBusy(true);
    try {
      await requestLeave({ ...f, end_date: f.end_date || f.start_date, user_id: f.user_id || null,
        start_time: f.partial ? f.start_time : null, end_time: f.partial ? f.end_time : null });
      toast.success(f.user_id ? "Leave entered, now approve it below" : "Leave requested. Your facility head will review it.");
      setF((x) => ({ ...x, start_date: "", end_date: "", reason: "" }));
      changed(); onDone();
    } catch (e) { toast.error(err(e, "Couldn't send the request")); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-5 space-y-4" data-testid="leave-form">
      <div className="font-display text-lg font-bold text-zinc-100">Ask for leave</div>
      {staff && (
        <label className="block text-xs text-zinc-400">For
          <select className={`${field} mt-1 w-full`} value={f.user_id} onChange={set("user_id")}>
            <option value="">Me</option>
            {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
          </select>
        </label>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs text-zinc-400">Type
          <select className={`${field} mt-1 w-full`} value={f.kind} onChange={set("kind")}>
            {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block text-xs text-zinc-400">First day
          <div className="mt-1"><DatePicker value={f.start_date} onChange={set("start_date")} data-testid="leave-start" /></div>
        </label>
        <label className="block text-xs text-zinc-400">Last day
          <div className="mt-1"><DatePicker value={f.end_date || f.start_date} onChange={set("end_date")} data-testid="leave-end" /></div>
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        <input type="checkbox" checked={f.partial} onChange={set("partial")} data-testid="leave-partial" />
        Part of the day only
      </label>
      {f.partial && (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          From <input type="time" className={field} value={f.start_time} onChange={set("start_time")} />
          to <input type="time" className={field} value={f.end_time} onChange={set("end_time")} />
          <span className="text-xs text-zinc-500">each day</span>
        </div>
      )}
      <label className="block text-xs text-zinc-400">Reason (optional)
        <textarea className={`${field} mt-1 w-full`} rows={2} maxLength={300} value={f.reason} onChange={set("reason")} />
      </label>
      <Button onClick={submit} disabled={busy} data-testid="leave-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {f.user_id ? "Enter leave" : "Send request"}
      </Button>
    </div>
  );
}

function LeaveRow({ lv, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[#1a1a1a] py-3 last:border-0">
      <div className="min-w-[200px] flex-1">
        <div className="text-sm text-zinc-100">{lv.user_name} <span className="text-zinc-500">· {KINDS[lv.kind] || lv.kind}{lv.half_day ? " · half day" : ""}</span>
          {lv.source === "yoactiv" && <span className="ml-2 text-[11px] text-zinc-500">from YoActiv ({lv.source_type})</span>}</div>
        <div className="text-xs text-zinc-400">{when(lv)}{lv.reason ? ` · ${lv.reason}` : ""}</div>
        {lv.status === "approved" && <Covers lv={lv} />}
        {lv.decision_note && <div className="text-xs text-zinc-500">Note: {lv.decision_note}</div>}
      </div>
      <Badge tone={TONES[lv.status]}>{lv.status}</Badge>
      {children}
    </div>
  );
}

function Covers({ lv }) {
  const c = lv.covers || {}; const n = lv.cover_names || {};
  const sess = (c.sessions || []).length;
  const bits = [];
  if (c.clients) bits.push(`clients → ${n[c.clients] || "?"}`);
  if (c.calls) bits.push(`calls → ${n[c.calls] || "?"}`);
  if (sess) bits.push(`${sess} session${sess > 1 ? "s" : ""} covered`);
  return bits.length ? <div className="text-xs text-emerald-300/80">{bits.join(" · ")}</div> : null;
}

function Mine({ reload, tick }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { myLeave().then((d) => setRows(d.leave)).catch(() => setRows([])); }, [tick]);
  const cancel = async (lv) => {
    try { await cancelLeave(lv.id); toast.success("Cancelled"); reload(); } catch (e) { toast.error(err(e, "Couldn't cancel")); }
  };
  if (!rows) return null;
  return (
    <div className="card-surface p-5" data-testid="leave-mine">
      <div className="font-display text-lg font-bold text-zinc-100 mb-2">My leave</div>
      {rows.length === 0 && <div className="text-sm text-zinc-500">No leave yet.</div>}
      {rows.map((lv) => (
        <LeaveRow key={lv.id} lv={lv}>
          {["pending", "approved"].includes(lv.status) && <Button variant="outline" size="sm" onClick={() => cancel(lv)}>Cancel</Button>}
        </LeaveRow>
      ))}
    </div>
  );
}

function Covering({ tick }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { myCovers().then((d) => setRows(d.covering)).catch(() => {}); }, [tick]);
  if (!rows.length) return null;
  return (
    <div className="card-surface p-5 space-y-4 border-emerald-500/30" data-testid="leave-covering">
      <div className="font-display text-lg font-bold text-zinc-100">You're covering</div>
      {rows.map((r) => (
        <div key={r.leave.id} className="space-y-2">
          <div className="text-sm text-zinc-200">
            {r.leave.user_name} · {when(r.leave)} {r.active ? <Badge tone="emerald">now</Badge> : <Badge tone="zinc">coming up</Badge>}
          </div>
          {r.calls && <div className="flex items-center gap-2 text-xs text-zinc-400"><PhoneCall className="h-3.5 w-3.5" /> Their incoming calls ring you during their shifts.</div>}
          {r.sessions.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-zinc-400"><Dumbbell className="mt-0.5 h-3.5 w-3.5" />
              <span>Sessions: {r.sessions.map((s) => fmtDay(s.date)).join(", ")} (they're on your clock-in roster)</span></div>
          )}
          {r.clients.length > 0 && (
            <div className="text-xs text-zinc-400">
              <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Their coaching clients
                {r.managed_members > 0 && <span className="text-zinc-500">· and {r.managed_members} members they manage</span>}</div>
              <ul className="mt-1 grid gap-1 sm:grid-cols-2">
                {r.clients.map((c) => <li key={c.id} className="text-zinc-300">{c.client_name} <span className="text-zinc-500">{c.phone} · {c.program || ""}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Picker({ value, onChange, people, none = "No cover", testid, already = [] }) {
  return (
    <select className={`${field} w-full`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testid}>
      <option value="">{none}</option>
      {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}{already.includes(p.name) ? " (already on it)" : ""}</option>)}
    </select>
  );
}

function Decide({ lv, onClose }) {
  const [imp, setImp] = useState(null);
  const [sessions, setSessions] = useState({});
  const [clients, setClients] = useState(null);
  const [calls, setCalls] = useState(null);
  const [notify, setNotify] = useState(!lv.partial);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const key = (s) => `${s.facility_id}|${s.date}|${s.session_schedule_id}`;
  useEffect(() => {
    leaveImpact(lv.id).then((d) => {
      setImp(d);
      setSessions(Object.fromEntries(d.sessions.map((s) => [key(s), s.cover || null])));
      setClients(lv.covers?.clients || null);
      setCalls(lv.covers?.calls || null);
      if (lv.status === "approved") setNotify(!!lv.notify_clients);
    }).catch((e) => toast.error(err(e, "Couldn't load")));
  }, [lv]);
  const setAll = (uid) => setSessions((x) => Object.fromEntries(Object.keys(x).map((k) => [k, uid])));
  const approve = async () => {
    const missing = imp.sessions.filter((s) => !sessions[key(s)]).length;
    if (missing && !window.confirm(`${missing} session${missing > 1 ? "s have" : " has"} no cover. Approve anyway?`)) return;
    setBusy(true);
    try {
      await approveLeave(lv.id, {
        sessions: imp.sessions.map((s) => ({ facility_id: s.facility_id, date: s.date, session_schedule_id: s.session_schedule_id, user_id: sessions[key(s)] || null })),
        clients_cover: clients, calls_cover: calls, notify_clients: notify && !!clients, note });
      toast.success(lv.status === "approved" ? "Covers updated" : "Leave approved");
      changed(); onClose(true);
    } catch (e) { toast.error(err(e, "Couldn't approve")); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await rejectLeave(lv.id, note); toast.success("Turned down"); changed(); onClose(true); }
    catch (e) { toast.error(err(e, "Couldn't update")); } finally { setBusy(false); }
  };
  const allPeople = imp?.candidates.people || [];
  const common = useMemo(() => {
    const v = Object.values(sessions); return v.length && v.every((x) => x === v[0]) ? v[0] : "";
  }, [sessions]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0f0f0f] border-[#262626]">
        <DialogHeader><DialogTitle>{lv.user_name} · {when(lv)}</DialogTitle></DialogHeader>
        {!imp ? <div className="py-6 text-sm text-zinc-500">Working out what this affects…</div> : (
          <div className="space-y-5 text-sm">
            {lv.reason && <div className="text-zinc-400">Reason: {lv.reason}</div>}
            <section className="space-y-2">
              <div className="flex items-center gap-2 font-semibold text-zinc-200"><Dumbbell className="h-4 w-4" /> Sessions ({imp.sessions.length})</div>
              {imp.sessions.length === 0 ? <div className="text-xs text-zinc-500">No rostered sessions in this time.</div> : (
                <>
                  <label className="block text-xs text-zinc-400">One cover for all
                    <Picker value={common} onChange={setAll} none="Choose…" people={allPeople} testid="leave-cover-all" />
                  </label>
                  <div className="divide-y divide-[#1a1a1a] rounded-md border border-[#1f1f1f]">
                    {imp.sessions.map((s) => (
                      <div key={key(s)} className="grid grid-cols-[1fr_180px] items-center gap-3 px-3 py-2">
                        <div className="text-xs text-zinc-300">{fmtDay(s.date)} {s.start_time} · {s.label}
                          <span className="text-zinc-500"> · {s.facility_name}{s.with.length ? ` · with ${s.with.join(", ")}` : ""}</span></div>
                        <Picker value={sessions[key(s)]} onChange={(v) => setSessions((x) => ({ ...x, [key(s)]: v }))}
                          people={imp.candidates.sessions[s.facility_id] || []} already={s.with} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
            {(imp.clients.length > 0 || imp.managed_members > 0 || clients) && <section className="space-y-2">
              <div className="flex items-center gap-2 font-semibold text-zinc-200"><Users className="h-4 w-4" /> Clients</div>
              <div className="text-xs text-zinc-400">{imp.clients.length} active coaching clients{imp.managed_members ? `, ${imp.managed_members} members they manage` : ""}.
                The cover looks after them during the leave; they go back automatically after.</div>
              <Picker value={clients} onChange={setClients} people={allPeople} testid="leave-cover-clients" />
              {clients && !lv.partial && (
                <label className="flex items-center gap-2 text-xs text-zinc-300">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                  Tell their clients in the app chat who's looking after them (posted the day before)
                </label>
              )}
            </section>}
            {imp.calls.lead_nurture && (
              <section className="space-y-2">
                <div className="flex items-center gap-2 font-semibold text-zinc-200"><PhoneCall className="h-4 w-4" /> Incoming calls</div>
                <div className="text-xs text-zinc-400">
                  {imp.calls.shifts.length ? `Shifts in this time: ${imp.calls.shifts.map((s) => `${fmtDay(s.date)} ${s.start}–${s.end}`).join(", ")}.` : "No call shifts set in FPA for this time."}
                  {" "}Exotel stops ringing {lv.user_name.split(" ")[0]} and rings the cover instead.</div>
                <Picker value={calls} onChange={setCalls} people={imp.candidates.calls} testid="leave-cover-calls" />
              </section>
            )}
            <label className="block text-xs text-zinc-400">Note (optional)
              <input className={`${field} mt-1 w-full`} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button onClick={approve} disabled={busy} data-testid="leave-approve">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {lv.status === "approved" ? "Save covers" : "Approve"}
              </Button>
              {lv.status === "pending" && <Button variant="outline" onClick={reject} disabled={busy}>Turn down</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Team({ tick, reload }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { listLeave().then((d) => setRows(d.leave)).catch(() => setRows([])); }, [tick]);
  if (!rows) return null;
  const today = new Date().toISOString().slice(0, 10);
  const pending = rows.filter((r) => r.status === "pending");
  const upcoming = rows.filter((r) => r.status === "approved" && r.end_date >= today);
  const cancel = async (lv) => {
    if (!window.confirm(`Cancel ${lv.user_name}'s leave? Their sessions go back to them from today.`)) return;
    try { await cancelLeave(lv.id); toast.success("Cancelled"); reload(); } catch (e) { toast.error(err(e, "Couldn't cancel")); }
  };
  return (
    <div className="card-surface p-5 space-y-4" data-testid="leave-team">
      <div className="font-display text-lg font-bold text-zinc-100">Team leave</div>
      <div>
        <div className="eyebrow mb-1">Waiting for you ({pending.length})</div>
        {pending.length === 0 && <div className="text-sm text-zinc-500">Nothing to approve.</div>}
        {pending.map((lv) => <LeaveRow key={lv.id} lv={lv}><Button size="sm" onClick={() => setOpen(lv)}>Review</Button></LeaveRow>)}
      </div>
      <div>
        <div className="eyebrow mb-1">Approved, current and coming up</div>
        {upcoming.length === 0 && <div className="text-sm text-zinc-500">No one is away.</div>}
        {upcoming.map((lv) => (
          <LeaveRow key={lv.id} lv={lv}>
            <Button size="sm" variant="outline" onClick={() => setOpen(lv)}>Change covers</Button>
            <Button size="sm" variant="outline" onClick={() => cancel(lv)}>Cancel</Button>
          </LeaveRow>
        ))}
      </div>
      {open && <Decide lv={open} onClose={(changed) => { setOpen(null); if (changed) reload(); }} />}
    </div>
  );
}

function ExotelBoard() {
  const [d, setD] = useState(null);
  useEffect(() => { exotelPresence().then(setD).catch(() => {}); }, []);
  if (!d || !d.people?.length) return null;
  return (
    <div className="card-surface p-5" data-testid="leave-exotel">
      <div className="font-display text-lg font-bold text-zinc-100">Who Exotel rings now</div>
      <div className="text-xs text-zinc-500 mb-2">
        {d.mode === "on" ? "FPA switches the lead nurture team ON and OFF from their shifts and leave." :
          d.mode === "dry" ? "Trial mode: FPA shows what it would switch, but changes nothing in Exotel yet." :
            "Not switched on yet: this is what FPA would do."}
        {d.error ? ` ${d.error}` : ""}
      </div>
      {d.people.map((p) => (
        <div key={p.user_id} className="flex items-center gap-3 border-b border-[#1a1a1a] py-2 text-sm last:border-0">
          <span className="flex-1 text-zinc-200">{p.name}</span>
          <span className="text-xs text-zinc-500">{p.why}</span>
          <Badge tone={p.want_on ? "emerald" : "zinc"}>{p.want_on ? "ON" : "OFF"}</Badge>
          {p.state && p.state !== "ok" && <span className="text-xs text-amber-300">{p.state}</span>}
        </div>
      ))}
    </div>
  );
}

const n1 = (x) => (Math.round((x || 0) * 10) / 10).toString();
const yearLabel = (y) => (y?.first?.slice(5) === "01-01" ? y.first.slice(0, 4) : `${fmtDay(y.first)} – ${fmtDay(y.last)}`);

// Where I am with my leave this year: what I've taken by type, and what's left once the policy is set.
function MyBalance({ tick }) {
  const [b, setB] = useState(null);
  useEffect(() => { leaveBalance().then(setB).catch(() => setB(null)); }, [tick]);
  if (!b) return null;
  const a = b.annual;
  const pct = a.allowance ? Math.min(100, Math.round((a.used / a.allowance) * 100)) : 0;
  return (
    <div className="card-surface p-5 space-y-4" data-testid="leave-balance">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-display text-lg font-bold text-zinc-100">My leave in {yearLabel(b.year)}</div>
        <div className="text-xs text-zinc-500">{b.employment_type === "part_time" && <span className="mr-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-sky-300">Part-time</span>}{b.country}{b.from_yoactiv ? " · includes leave entered in YoActiv" : ""}</div>
      </div>
      {b.policy_set ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <span className={`font-display text-4xl font-bold tabular-nums ${a.left < 0 ? "text-rose-300" : "text-emerald-300"}`}>{n1(a.left)}</span>
            <span className="text-sm text-zinc-400 mb-1">{b.rules.annual_rule === "accrual" ? `left of the ${n1(a.allowance)} annual days earned so far` : `of ${n1(a.allowance)} annual days left`}</span>
          </div>
          {a.note && <div className="text-xs text-amber-300">{a.note === "joining date needed" ? "Your joining date isn't in FPA yet, so your allowance can't be worked out. Ask your facility head to add it." : a.note}</div>}
          <div className="h-2 rounded-full bg-[#1f1f1f] overflow-hidden"><div className={`h-full ${a.left < 0 ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-zinc-500">{n1(a.used)} used{b.rules.personal_counts_as_annual ? " (annual and personal leave both count)" : " (annual leave only)"}
            {b.pending ? ` · ${n1(b.pending)} more waiting for approval` : ""}
            {b.sick.allowance ? ` · sick: ${n1(b.sick.left)} of ${n1(b.sick.allowance)} left` : ""}
            {b.bereavement?.allowance ? ` · bereavement: ${n1(b.bereavement.left)} of ${n1(b.bereavement.allowance)} left` : ""}</div>
          {b.rules.note && <div className="text-xs text-zinc-500">{b.rules.note}</div>}
        </div>
      ) : (
        <p className="text-sm text-amber-300">Your leave allowance isn't set up in FPA yet. Here is what you've taken so far.</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(KINDS).map(([k, label]) => (
          <div key={k} className="rounded-lg border border-[#262626] p-3">
            <div className="text-xs text-zinc-500">{label}</div>
            <div className="font-display text-2xl font-bold tabular-nums text-zinc-100">{n1(b.taken[k])}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-zinc-500">{b.rules?.calendar_days ? "Days are calendar days" : "Days don't count Sundays"}{(b.rules?.holidays || []).length ? `${b.rules?.calendar_days ? ", not counting" : " or"} the ${b.rules.holidays.length} national holidays` : ""}. A half day counts as 0.5.</p>
    </div>
  );
}

// Facility heads: everyone in the gym. Admins can see every gym.
function TeamBalances({ tick, admin }) {
  const [all, setAll] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => { leaveBalances(all).then(setD).catch(() => setD(null)); }, [tick, all]);
  if (!d) return null;
  return (
    <div className="card-surface p-5 space-y-3" data-testid="leave-team-balances">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-display text-lg font-bold text-zinc-100">Team leave this year</div>
        {admin && <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />All gyms</label>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-zinc-500 text-left">
            <th className="py-2 pr-3">Name</th>{all && <th className="py-2 pr-3">Gym</th>}
            {Object.values(KINDS).map((l) => <th key={l} className="py-2 pr-3 text-right">{l}</th>)}
            <th className="py-2 pr-3 text-right">Waiting</th><th className="py-2 pr-3 text-right">Annual left</th><th className="py-2 text-right">Joined</th></tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.user_id} className="border-t border-[#1a1a1a]">
                <td className="py-2 pr-3 text-zinc-100">{r.name}
                  {admin ? (
                    <select value={r.employment_type || "full_time"} className={`${field} ml-2 text-[11px] py-0.5`} data-testid={`employment-${r.user_id}`}
                      onChange={(e) => setEmployment(r.user_id, e.target.value).then(() => { toast.success(`${r.name}: ${e.target.value === "part_time" ? "part-time" : "full-time"}`); leaveBalances(all).then(setD); }).catch((x) => toast.error(err(x, "Couldn't save")))}>
                      <option value="full_time">Full-time</option><option value="part_time">Part-time</option></select>
                  ) : r.employment_type === "part_time" && <span className="ml-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[11px] text-sky-300">Part-time</span>}</td>
                {all && <td className="py-2 pr-3 text-zinc-400">{r.gym}</td>}
                {Object.keys(KINDS).map((k) => <td key={k} className="py-2 pr-3 text-right tabular-nums text-zinc-300">{r.taken[k] ? n1(r.taken[k]) : "–"}</td>)}
                <td className="py-2 pr-3 text-right tabular-nums text-amber-300">{r.pending ? n1(r.pending) : "–"}</td>
                <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${r.annual.left == null ? "text-zinc-600" : r.annual.left < 0 ? "text-rose-300" : "text-emerald-300"}`}
                  title={r.annual.note || ""}>
                  {r.annual.left == null ? (r.annual.note === "joining date needed" ? "add joining date" : "not set") : n1(r.annual.left)}</td>
                <td className="py-2 text-right">
                  <input type="date" defaultValue={r.joined_on || ""} className={`${field} w-36 text-xs`} data-testid={`joined-${r.user_id}`}
                    onBlur={(e) => { if ((e.target.value || null) !== (r.joined_on || null)) setJoinedOn(r.user_id, e.target.value).then(() => { toast.success(`Joining date saved for ${r.name}`); setD(null); leaveBalances(all).then(setD); }).catch((x) => toast.error(err(x, "Couldn't save"))); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const holidaysText = (list) => (list || []).map((h) => `${h.date} ${h.name || ""}`.trim()).join("\n");
const parseHolidays = (text) => (text || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const m = l.match(/^(\d{4}-\d{2}-\d{2})\s*(.*)$/);
  if (!m) throw new Error(`Holiday line needs a date like 2026-01-26: "${l}"`);
  return { date: m[1], name: m[2].trim() };
});

// Admins: the leave policy per country. Balances use it straight away.
function PolicyEditor({ onSaved }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getLeavePolicy().then((d) => setP(d.countries)).catch(() => {}); }, []);
  if (!p) return null;
  const set = (c, k, v) => setP({ ...p, [c]: { ...p[c], [k]: v } });
  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(p).map(([c, r]) => [c, { ...r,
        annual_days: r.annual_days === "" || r.annual_days == null ? null : Number(r.annual_days),
        sick_days: r.sick_days === "" || r.sick_days == null ? null : Number(r.sick_days), year_start_month: Number(r.year_start_month) || 1,
        bereavement_days: r.bereavement_days === "" || r.bereavement_days == null ? null : Number(r.bereavement_days),
        holidays: parseHolidays(r.holidays_text ?? holidaysText(r.holidays)) }]).map(([c, r]) => { const { holidays_text, ...rest } = r; return [c, rest]; }));
      setP((await saveLeavePolicy(clean)).countries); toast.success("Leave policy saved"); onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); } finally { setBusy(false); }
  };
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return (
    <div className="card-surface p-5 space-y-4" data-testid="leave-policy">
      <div className="font-display text-lg font-bold text-zinc-100">Leave policy</div>
      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(p).map(([c, r]) => (
          <div key={c} className="rounded-lg border border-[#262626] p-3 space-y-2">
            <div className="text-sm font-semibold text-zinc-100">{c === "United Arab Emirates" ? "Dubai (UAE)" : c}</div>
            <label className="block text-xs text-zinc-400">Annual days a year
              <input type="number" min="0" className={`${field} mt-1 w-full`} value={r.annual_days ?? ""} onChange={(e) => set(c, "annual_days", e.target.value)} /></label>
            <label className="block text-xs text-zinc-400">Sick days a year (empty = no separate limit)
              <input type="number" min="0" className={`${field} mt-1 w-full`} value={r.sick_days ?? ""} onChange={(e) => set(c, "sick_days", e.target.value)} /></label>
            <label className="block text-xs text-zinc-400">Bereavement days (separate from annual)
              <input type="number" min="0" className={`${field} mt-1 w-full`} value={r.bereavement_days ?? ""} onChange={(e) => set(c, "bereavement_days", e.target.value)} /></label>
            <label className="block text-xs text-zinc-400">Annual allowance
              <select className={`${field} mt-1 w-full`} value={r.annual_rule || "fixed"} onChange={(e) => set(c, "annual_rule", e.target.value)}>
                <option value="fixed">Same for everyone</option>
                <option value="accrual">Earned monthly: the annual days / 12 for each month worked</option>
                <option value="uae_service">UAE law: none under 6 months, 2 a month to 1 year, then the full days</option></select></label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={!!r.calendar_days} onChange={(e) => set(c, "calendar_days", e.target.checked)} />
              Count calendar days (Sundays count too)</label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={!!r.personal_counts_as_annual} onChange={(e) => set(c, "personal_counts_as_annual", e.target.checked)} />
              Personal leave comes out of annual</label>
            <label className="block text-xs text-zinc-400">Leave year starts
              <select className={`${field} mt-1 w-full`} value={r.year_start_month || 1} onChange={(e) => set(c, "year_start_month", e.target.value)}>
                {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></label>
            <label className="block text-xs text-zinc-400">National holidays, one a line: 2026-01-26 Republic Day (not counted as leave)
              <textarea rows={5} className={`${field} mt-1 w-full font-mono text-[11px]`} value={r.holidays_text ?? holidaysText(r.holidays)}
                onChange={(e) => set(c, "holidays_text", e.target.value)} /></label>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save policy</Button>
    </div>
  );
}

export default function Leave() {
  const { user, effectiveRole } = useAuth();
  const head = atLeast(effectiveRole || user?.role, "manager");
  const [tick, setTick] = useState(0);
  const [staff, setStaff] = useState(null);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (!head) return;
    getRoster().then((d) =>
      setStaff((d.staff || []).filter((s) => s.user_id !== user?.user_id).sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
  }, [head, user?.user_id]);
  return (
    <div className="space-y-6 p-4 md:p-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <CalendarOff className="h-6 w-6 text-zinc-400" />
        <div>
          <h1 className="font-display text-2xl font-bold text-zinc-100">Leave</h1>
          <p className="text-sm text-zinc-500">Ask for leave, and see who covers sessions, clients and calls while someone's away.</p>
        </div>
      </div>
      <MyBalance tick={tick} />
      <Covering tick={tick} />
      {head && <Team tick={tick} reload={reload} />}
      {head && <TeamBalances tick={tick} admin={atLeast(user?.role, "admin")} />}
      <div className="grid gap-6 lg:grid-cols-2">
        <AskForm staff={head ? staff : null} onDone={reload} />
        <Mine tick={tick} reload={reload} />
      </div>
      {head && <ExotelBoard />}
      {atLeast(user?.role, "admin") && <PolicyEditor onSaved={reload} />}
    </div>
  );
}
